import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Stack,
} from "@mui/material";
import {
  CheckCircle,
  AccessTime,
  Error,
  Pending,
  Payment,
  LocalShipping,
} from "@mui/icons-material";

/**
 * StatusIndicator - Displays quote status with visual indicators and progress
 * Mobile-first responsive design with clear status communication
 */
export const StatusIndicator = ({ status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: "warning",
        icon: <Pending />,
        label: "Pending Review",
        description: "Your quote is being reviewed by our team",
        progress: 25,
      },
      sent: {
        color: "info",
        icon: <AccessTime />,
        label: "Quote Sent",
        description: "Your quote has been sent and is awaiting your response",
        progress: 50,
      },
      negotiating: {
        color: "primary",
        icon: <AccessTime />,
        label: "Under Negotiation",
        description: "We're discussing the details of your quote",
        progress: 60,
      },
      approved: {
        color: "success",
        icon: <CheckCircle />,
        label: "Quote Approved",
        description: "Your quote has been approved and is ready for acceptance",
        progress: 75,
      },
      accepted: {
        color: "success",
        icon: <CheckCircle />,
        label: "Quote Accepted",
        description: "Great! Your quote has been accepted. Proceed to payment",
        progress: 90,
      },
      pending_payment: {
        color: "info",
        icon: <Payment />,
        label: "Payment Pending",
        description:
          "We're verifying your payment. You'll be notified once confirmed",
        progress: 95,
      },
      completed: {
        color: "success",
        icon: <LocalShipping />,
        label: "Order Completed",
        description: "Your order has been completed successfully",
        progress: 100,
      },
      rejected: {
        color: "error",
        icon: <Error />,
        label: "Quote Rejected",
        description: "This quote has been rejected",
        progress: 0,
      },
      expired: {
        color: "default",
        icon: <Error />,
        label: "Quote Expired",
        description: "This quote has expired. Please request a new one",
        progress: 0,
      },
    };

    return (
      configs[status] || {
        color: "default",
        icon: <Pending />,
        label: "Unknown Status",
        description: "Status information is not available",
        progress: 0,
      }
    );
  };

  const config = getStatusConfig(status);

  if (!status) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Loading status...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: `${config.color}.50`,
              color: `${config.color}.main`,
            }}
          >
            {config.icon}
          </Box>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {config.label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
          </Box>
          <Chip
            label={status.toUpperCase()}
            color={config.color}
            variant="outlined"
            size="small"
          />
        </Box>

        {/* Progress Bar */}
        <Box mb={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {config.progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={config.progress}
            color={config.color}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: `${config.color}.100`,
            }}
          />
        </Box>

        {/* Status-specific information */}
        {status === "pending_payment" && (
          <Box
            sx={{
              p: 2,
              bgcolor: "info.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "info.200",
            }}
          >
            <Typography variant="body2" color="info.main">
              <strong>What happens next?</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • We'll verify your payment within 24 hours
              <br />
              • You'll receive a confirmation email/SMS
              <br />• Our team will contact you to schedule the service
            </Typography>
          </Box>
        )}

        {status === "accepted" && (
          <Box
            sx={{
              p: 2,
              bgcolor: "success.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "success.200",
            }}
          >
            <Typography variant="body2" color="success.main">
              <strong>Next Steps:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              • Complete payment to secure your booking
              <br />
              • We'll confirm your service date and time
              <br />• Our team will be in touch with final details
            </Typography>
          </Box>
        )}

        {status === "expired" && (
          <Box
            sx={{
              p: 2,
              bgcolor: "error.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "error.200",
            }}
          >
            <Typography variant="body2" color="error.main">
              <strong>Quote Expired</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This quote is no longer valid. Please contact us to request a new
              quote with updated pricing.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

